/**
 * TopUpModal — THE top-up popup (2026-08-14 unification).
 *
 * Owner directive: every "pay for more PRO Work Units" entry point — the
 * wallet page buttons, the low-balance banners, chat, `?action=add-tokens`
 * deep links — opens THIS one modal, so the top-up experience is identical
 * everywhere. Design follows the wallet page's platform (gold) design
 * language; functionality is the superset of the two previous popups:
 *
 *   - by-USD / by-work-units entry modes (tabs)
 *   - suggested amounts (owner-directed ladder: $100 / $250 / $500 / $1,000)
 *   - saved cards with one-click selection + "use a new card" CardElement
 *   - live amount / transaction fee / total breakdown with pr0 estimate
 *   - in-place success (no navigation; realtime balance watcher)
 *   - auto top-up, ON BY DEFAULT for never-configured users but only
 *     ENROLLED when the user confirms by completing a top-up (never from
 *     merely opening the modal; a stored "off" choice is always respected;
 *     unchecking while enrolled disables immediately)
 *
 * Rendered once by TopUpModalProvider inside a Stripe <Elements> boundary,
 * portal-mounted to document.body.
 *
 * Draws only (the top-up dialog part 1, PV10, 2026-10-13): what it does is
 * useTopUpDialog's (src/hooks/useTopUpDialog.ts), which the new design's
 * top-up dialog draws from too — the amount, the cards, the charge
 * (paymentIntent → confirmCardPayment → confirmAndCreditPayment, money math
 * unchanged), the credit landing, the promo code and the auto top-up offer.
 * Fee is $0.30 + 2.9%; minimum top-up is $2. The charged-token derivation
 * uses the live rate; the pr0 numbers shown are display-only at the
 * published Standard Rate ($2.18 / pr0).
 */

import React from 'react';
import ReactDOM from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { APP_OVERLAY_Z_INDEX } from '../../styles/layers';
import { CardElement } from '@stripe/react-stripe-js';
import { useTopUpDialog, MIN_TOP_UP_USD } from '../../hooks/useTopUpDialog';
import {
  usdToWu,
  wuToUsd,
  formatWu,
  WU_LABEL,
  STANDARD_RATE_USD_PER_WU,
} from '../../config/workUnits';
import { formatUsd } from '../common/CopyableAmount/utils';
import Pr0Unit from '../common/Pr0Unit';

// ============================================================================
// Animations + styled components — wallet-page (gold) design language
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Opened straight from the chat (GlobalChatSystem's low-balance path),
     so it must clear the pane in every mode — its old hand-picked number
     cleared the floating overlay but not the full-screen takeover. */
  z-index: ${APP_OVERLAY_Z_INDEX};
  animation: ${fadeIn} 0.18s ease;
  padding: 1rem;
  overflow-y: auto;

  @media (max-width: 480px) {
    align-items: flex-start;
    padding-top: calc(env(safe-area-inset-top, 0px) + 1rem);
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.75rem 2rem;
  width: 100%;
  /* Roomy enough that a full top-up (amount · payment · fee · auto top-up)
     fits without the fiddly incremental scrolling of the narrow card. */
  max-width: clamp(320px, 94vw, 760px);
  max-height: 92vh;
  overflow-y: auto;
  /* Nothing inside may push the card sideways — a horizontal scrollbar here
     clipped the right edge of the fee box and the auto top-up fields. */
  overflow-x: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  animation: ${slideUp} 0.22s ease;
  text-align: left;
  box-sizing: border-box;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    border-radius: 12px;
    max-height: 94vh;
  }
`;

/**
 * Desktop body layout: what you're buying on the left, how you're paying on
 * the right. Halves the scroll height without hiding anything; stacks to the
 * original single column on narrow screens, where the order is unchanged.
 */
const BodyGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  column-gap: 1.6rem;

  @media (min-width: 720px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
`;

/** Grid/flex children need an explicit zero min so long content can't blow out the row. */
const Col = styled.div`
  min-width: 0;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.35rem;
`;

const ModalTitle = styled.h3`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--brand-text);
  margin: 0;
  text-transform: lowercase;
`;

const ModalSubtitle = styled.p`
  margin: 0.25rem 0 1.25rem 0;
  color: var(--brand-text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModeToggle = styled.div`
  display: flex;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 0.25rem;
  margin-bottom: 1.5rem;
`;

const ModeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: lowercase;
  background: ${(p) => (p.$active ? 'white' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--brand-gold)' : 'var(--brand-text-secondary)')};
  box-shadow: ${(p) => (p.$active ? '0 2px 4px rgba(0,0,0,0.1)' : 'none')};

  &:hover {
    color: var(--brand-gold);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.15rem;
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 600;
  color: var(--brand-text);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const PresetGrid = styled.div`
  display: grid;
  /* Two across at every width: each chip carries a dollar figure AND its pr0
     equivalent, which four-across squeezed into an overflow. */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const PresetButton = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0.6rem 0.25rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(p) => (p.$active ? 'linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%)' : 'white')};
  border: 2px solid ${(p) => (p.$active ? 'var(--brand-gold-hover)' : 'var(--brand-line)')};

  &:hover {
    border-color: var(--brand-gold);
  }
`;

const PresetMain = styled.span<{ $active?: boolean }>`
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--brand-text);
`;

const PresetSub = styled.span<{ $active?: boolean }>`
  font-size: 0.7rem;
  color: ${(p) => (p.$active ? '#3d2f10' : '#6b7280')};
  text-transform: lowercase;
`;

const InputWithAddon = styled.div`
  display: flex;
  align-items: stretch;
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: white;
  min-width: 0;
  max-width: 100%;

  &:focus-within {
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }
`;

const InputAddon = styled.span<{ $position: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0.875rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  color: #64748b;
  font-weight: 600;
  font-size: 0.9rem;
  border-right: ${(p) => (p.$position === 'left' ? '1px solid #e2e8f0' : 'none')};
  border-left: ${(p) => (p.$position === 'right' ? '1px solid #e2e8f0' : 'none')};
  user-select: none;
  min-width: ${(p) => (p.$position === 'right' ? '3.5rem' : 'auto')};
`;

const AdornedInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  font-size: 1rem;
  background: transparent;
  min-width: 0;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--brand-text-muted);
    text-transform: lowercase;
  }
`;

const GoldNote = styled.div`
  background: var(--brand-canvas);
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin: 0 0 1.15rem;
  border-left: 4px solid var(--brand-gold);
`;

const GoldNoteText = styled.p`
  color: var(--brand-text-secondary);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
  text-transform: lowercase;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
    color: var(--brand-gold);
    flex-shrink: 0;
  }

  strong {
    color: var(--brand-text);
  }
`;

const MethodRow = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: white;
  border: 2px solid ${(p) => (p.$active ? 'var(--brand-gold)' : 'var(--brand-line)')};
  box-shadow: ${(p) => (p.$active ? '0 0 0 3px rgba(198, 167, 94, 0.12)' : 'none')};
  cursor: pointer;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: var(--brand-text);
  transition: all 0.2s ease;
  text-transform: lowercase;

  &:hover {
    border-color: var(--brand-gold);
  }

  span[aria-hidden] {
    color: var(--brand-gold);
    font-weight: 700;
  }
`;

const CardElementBox = styled.div`
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  background: white;
  padding: 0.85rem 1rem;
  margin-top: 0.25rem;
  transition: all 0.3s ease;

  &:focus-within {
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }
`;

const FeeBox = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin: 0.5rem 0 1rem 0;
  border-left: 4px solid #9ca3af;
`;

const FeeRow = styled.div<{ $total?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #555;
  font-size: 0.9rem;
  text-transform: lowercase;
  margin: 0.15rem 0;
  ${(p) =>
    p.$total &&
    css`
      font-weight: 600;
      color: var(--brand-text);
    `}
`;

const FeeAmount = styled.span`
  color: var(--brand-text);
  font-weight: 600;
`;

const HelperText = styled.p`
  margin: 0.4rem 0 0 0;
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: lowercase;
`;

const ErrorText = styled.p`
  margin: 0.4rem 0 0 0;
  font-size: 0.8rem;
  color: #b91c1c;
  text-transform: lowercase;
`;

const AutoSection = styled.div`
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  padding: 1rem;
  margin: 0 0 1.15rem;
`;

const SwitchRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.9rem;
  color: var(--brand-text);
  cursor: pointer;
  text-transform: lowercase;

  input {
    accent-color: var(--brand-gold);
    margin-top: 2px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  strong {
    font-weight: 600;
  }
`;

const AutoFieldRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.85rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const AutoField = styled.div`
  flex: 1;
  min-width: 0;
`;

const AutoFieldLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.3rem;
  text-transform: lowercase;
`;

// ── Promo code (PR0-… wallet-credit codes) ──────────────────────────────────

const PromoRow = styled.div`
  margin: 0 0 1.15rem;
`;

const PromoToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  color: #8a7648;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  text-transform: lowercase;

  &:hover {
    color: var(--brand-text);
  }
`;

const PromoForm = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: stretch;
  margin-top: 0.6rem;
  flex-wrap: wrap;
`;

const PromoInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.7rem 0.9rem;
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  letter-spacing: 0.06em;
  text-transform: lowercase;
  background: white;

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }

  &::placeholder {
    color: #b3b3b3;
    letter-spacing: 0.04em;
  }
`;

const PromoApply = styled.button`
  padding: 0.7rem 1.2rem;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-transform: lowercase;
  background: white;
  color: var(--brand-text);
  border: 2px solid var(--brand-gold);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  ${(p) =>
    p.variant === 'primary'
      ? css`
          background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
          color: var(--brand-text);
          border: none;

          &:hover:not(:disabled) {
            background: linear-gradient(135deg, var(--brand-gold-hover) 0%, #a07d3a 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(198, 167, 94, 0.3);
          }
        `
      : css`
          background: white;
          color: var(--brand-text-secondary);
          border: 2px solid var(--brand-line);

          &:hover:not(:disabled) {
            background: #f5f5f5;
            border-color: #ccc;
          }
        `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const TrialNote = styled.div<{ $warn?: boolean }>`
  background: ${(p) => (p.$warn ? '#FEF3C7' : '#EFF6FF')};
  border: 1px solid ${(p) => (p.$warn ? '#F59E0B' : '#BFDBFE')};
  color: ${(p) => (p.$warn ? '#78350F' : '#1E3A5F')};
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
  font-size: 0.82rem;
  line-height: 1.55;
  margin-bottom: 1rem;
`;

const SuccessWrap = styled.div`
  text-align: center;
  padding: 0.75rem 0.5rem 0.25rem;
`;

const SuccessCircle = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #faf6ea 0%, #f0e6c8 100%);
  border: 2px solid var(--brand-gold);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 28px;
    height: 28px;
    color: var(--brand-gold-hover);
  }
`;

// ============================================================================
// Component
// ============================================================================

export interface TopUpModalProps {
  /** Optional free-text reason describing what triggered the modal (telemetry/UX). */
  reason?: string | null;
  /** Close the modal (provider clears isOpen). */
  onClose: () => void;
}

export const TopUpModal: React.FC<TopUpModalProps> = ({ reason, onClose }) => {
  const {
    status,
    orgBlocked,
    trialBlocked,
    trial: trialStatus,
    mode,
    switchMode,
    presets,
    selectedPreset,
    choosePreset,
    runwayFor,
    customAmount,
    typeCustomAmount,
    focusCustomAmount,
    effectiveAmountUsd,
    fee,
    total,
    estWu,
    belowMin,
    isBusy,
    loadingMethods,
    savedCards,
    orgSharedCards,
    selectedPmId,
    selectedSharedPmId,
    useNewCard,
    chooseSharedCard,
    chooseSavedCard,
    chooseNewCard,
    orgTopUpTarget,
    uid,
    autoWanted,
    handleAutoToggle,
    autoEnabled,
    autoLoading,
    autoError,
    atuThreshold,
    setAtuThreshold,
    atuAmount,
    setAtuAmount,
    autoThresholdUsd,
    autoAmountUsd,
    commitThreshold,
    commitAmount,
    promoOpen,
    openPromo,
    promoCode,
    typePromoCode,
    promoBusy,
    promoError,
    handleRedeemPromo,
    codeCreditedWu,
    successNote,
    errorMsg,
    handleSubmit,
    onBackdropMouseDown,
    activateAccount,
    portalTarget,
  } = useTopUpDialog({ onClose });

  const content =
    status === 'success' ? (
      <SuccessWrap>
        <SuccessCircle>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </SuccessCircle>
        <ModalTitle as="h3">{codeCreditedWu != null ? 'code applied' : 'top-up complete'}</ModalTitle>
        <ModalSubtitle>
          {codeCreditedWu != null ? (
            <>
              {formatWu(codeCreditedWu)} <Pr0Unit /> added to your wallet. you can keep working
              right where you left off.
            </>
          ) : successNote ? (
            <>
              {successNote} you can keep working right where you left off.
              {autoWanted && autoEnabled ? ' auto top-up is active.' : ''}
            </>
          ) : (
            <>
              your balance has been updated. you can keep working right where you left off.
              {autoWanted && autoEnabled ? ' auto top-up is active.' : ''}
            </>
          )}
        </ModalSubtitle>
        <ModalActions style={{ justifyContent: 'center' }}>
          <ModalButton variant="primary" type="button" onClick={onClose}>
            done
          </ModalButton>
        </ModalActions>
      </SuccessWrap>
    ) : orgBlocked ? (
      <>
        <ModalHeader>
          <ModalTitle>this workspace&apos;s wallet</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalSubtitle>
          topping up this organization&apos;s wallet needs the billing permission —
          ask the owner for a top-up from the subscription page, or to grant you
          billing access.
        </ModalSubtitle>
        <ModalActions style={{ justifyContent: 'center' }}>
          <ModalButton variant="primary" type="button" onClick={onClose}>
            got it
          </ModalButton>
        </ModalActions>
      </>
    ) : trialBlocked ? (
      <>
        <ModalHeader>
          <ModalTitle>your internship has ended</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalSubtitle>
          an ended internship can't top up pr0. to keep your workspace, hire your
          Prest0n on the subscription page — everything stays.
        </ModalSubtitle>
        <ModalActions>
          <ModalButton variant="secondary" type="button" onClick={onClose}>
            cancel
          </ModalButton>
          <ModalButton
            variant="primary"
            type="button"
            onClick={activateAccount}
          >
            activate my account
          </ModalButton>
        </ModalActions>
      </>
    ) : (
      <>
        <ModalHeader>
          <ModalTitle>
            top up <Pr0Unit />
          </ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close" disabled={status === 'processing'}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </ModalHeader>
        <ModalSubtitle>top up your wallet without leaving this page.</ModalSubtitle>

        {trialStatus.isTrialUser && trialStatus.isActive && (
          <TrialNote role="note" $warn={trialStatus.isExpiringSoon}>
            {trialStatus.isExpiringSoon
              ? `heads up — your internship ends in ${trialStatus.daysRemaining} day${trialStatus.daysRemaining === 1 ? '' : 's'}${trialStatus.endsOn ? ` (${trialStatus.endsOn})` : ''}. after that, an ended internship can't top up pr0 — your subscription page has what comes next.`
              : `you're on an internship${trialStatus.endsOn ? ` through ${trialStatus.endsOn}` : ''}. once it ends, it can't top up pr0 — your subscription page has what comes next.`}
          </TrialNote>
        )}

        <ModeToggle role="tablist" aria-label="top-up entry mode">
          <ModeButton type="button" role="tab" aria-selected={mode === 'usd'} $active={mode === 'usd'} onClick={() => switchMode('usd')}>
            by usd amount
          </ModeButton>
          <ModeButton type="button" role="tab" aria-selected={mode === 'wu'} $active={mode === 'wu'} onClick={() => switchMode('wu')}>
            by work units
          </ModeButton>
        </ModeToggle>

        <BodyGrid>
        <Col>
        <FormGroup>
          <FormLabel>{mode === 'usd' ? 'amount to spend' : 'work units to top up'}</FormLabel>
          <PresetGrid>
            {presets.map((amt) => (
              <PresetButton
                key={amt}
                type="button"
                $active={selectedPreset === amt}
                onClick={() => choosePreset(amt)}
              >
                {mode === 'usd' ? (
                  <>
                    <PresetMain>${amt.toLocaleString('en-US')}</PresetMain>
                    <PresetSub $active={selectedPreset === amt}>
                      {/* What the money BUYS beats what it converts to: a
                          person can consent to "about a week", not to 229 pr0. */}
                      {runwayFor[amt] ?? <>≈ {formatWu(usdToWu(amt))} {WU_LABEL}</>}
                    </PresetSub>
                  </>
                ) : (
                  <>
                    <PresetMain>
                      {amt.toLocaleString('en-US')} {WU_LABEL}
                    </PresetMain>
                    <PresetSub $active={selectedPreset === amt}>{formatUsd(wuToUsd(amt))}</PresetSub>
                  </>
                )}
              </PresetButton>
            ))}
          </PresetGrid>
          <InputWithAddon>
            {mode === 'usd' && <InputAddon $position="left">$</InputAddon>}
            <AdornedInput
              type="number"
              min={mode === 'usd' ? MIN_TOP_UP_USD : 1}
              step="1"
              inputMode="decimal"
              placeholder="custom amount"
              value={selectedPreset === 'custom' ? customAmount : ''}
              onChange={(e) => typeCustomAmount(e.target.value)}
              onFocus={focusCustomAmount}
            />
            <InputAddon $position="right">{mode === 'usd' ? 'USD' : <Pr0Unit />}</InputAddon>
          </InputWithAddon>
          {effectiveAmountUsd > 0 && belowMin && (
            <HelperText>minimum top-up is ${MIN_TOP_UP_USD.toFixed(2)}.</HelperText>
          )}
        </FormGroup>

        <GoldNote>
          <GoldNoteText>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            {effectiveAmountUsd > 0 ? (
              <span>
                <strong>{formatUsd(effectiveAmountUsd)}</strong> ≈{' '}
                <strong>
                  {formatWu(estWu)} <Pr0Unit />
                </strong>{' '}
                (standard rate ${STANDARD_RATE_USD_PER_WU.toFixed(2)} / {WU_LABEL})
              </span>
            ) : (
              <span>
                standard rate ${STANDARD_RATE_USD_PER_WU.toFixed(2)} / {WU_LABEL}
              </span>
            )}
          </GoldNoteText>
        </GoldNote>
        </Col>

        <Col>
        <FormGroup>
          <FormLabel>payment method</FormLabel>
          {loadingMethods ? (
            <HelperText>loading saved cards…</HelperText>
          ) : (
            <>
              {orgSharedCards.length > 0 && (
                <>
                  <HelperText>shared with this organization</HelperText>
                  {orgSharedCards.map((c) => (
                    <MethodRow
                      key={c.id}
                      type="button"
                      $active={!useNewCard && selectedSharedPmId === c.id}
                      onClick={() => chooseSharedCard(c.id)}
                    >
                      <span>
                        {(c.brand || 'card').toLowerCase()} •••• {c.last4 || '????'}
                        {c.mine ? ' — your card' : c.ownerLabel ? ` — shared by ${c.ownerLabel}` : ' — shared'}
                      </span>
                      {!useNewCard && selectedSharedPmId === c.id && <span aria-hidden>✓</span>}
                    </MethodRow>
                  ))}
                  {savedCards.length > 0 && <HelperText>your cards</HelperText>}
                </>
              )}
              {savedCards.map((c) => (
                <MethodRow
                  key={c.id}
                  type="button"
                  $active={!useNewCard && selectedPmId === c.id}
                  onClick={() => chooseSavedCard(c.id)}
                >
                  <span>
                    {(c.brand || 'card').toLowerCase()} •••• {c.last4 || '????'}
                  </span>
                  {!useNewCard && selectedPmId === c.id && <span aria-hidden>✓</span>}
                </MethodRow>
              ))}
              <MethodRow type="button" $active={useNewCard} onClick={chooseNewCard}>
                <span>use a new card</span>
                {useNewCard && <span aria-hidden>✓</span>}
              </MethodRow>
              {useNewCard && (
                <CardElementBox>
                  <CardElement options={{ hidePostalCode: true }} />
                </CardElementBox>
              )}
            </>
          )}
        </FormGroup>

        {effectiveAmountUsd >= MIN_TOP_UP_USD && (
          <FeeBox role="note" aria-live="polite">
            <FeeRow>
              <span>amount</span>
              <FeeAmount>{formatUsd(effectiveAmountUsd)}</FeeAmount>
            </FeeRow>
            <FeeRow>
              <span>transaction fee</span>
              <FeeAmount>{formatUsd(fee)}</FeeAmount>
            </FeeRow>
            <FeeRow $total>
              <span>total charged</span>
              <FeeAmount>{formatUsd(total)}</FeeAmount>
            </FeeRow>
            <HelperText>includes $0.30 + 2.9% of the transaction amount</HelperText>
            {orgTopUpTarget && (
              <HelperText>
                charges your card and credits this workspace&apos;s wallet
              </HelperText>
            )}
          </FeeBox>
        )}
        </Col>
        </BodyGrid>

        {!orgTopUpTarget && (
        <AutoSection>
          <SwitchRow>
            <input
              type="checkbox"
              checked={autoWanted}
              disabled={autoLoading || !uid || isBusy}
              onChange={(e) => handleAutoToggle(e.target.checked)}
            />
            <span>
              <strong>auto top-up</strong> — automatically top up when my balance runs low
            </span>
          </SwitchRow>
          <AutoFieldRow>
            <AutoField>
              <AutoFieldLabel>when below (usd)</AutoFieldLabel>
              <InputWithAddon>
                <InputAddon $position="left">$</InputAddon>
                <AdornedInput
                  type="number"
                  min={0}
                  step="1"
                  value={atuThreshold}
                  disabled={!autoWanted}
                  onChange={(e) => setAtuThreshold(e.target.value)}
                  onBlur={commitThreshold}
                />
              </InputWithAddon>
            </AutoField>
            <AutoField>
              <AutoFieldLabel>top up by (usd)</AutoFieldLabel>
              <InputWithAddon>
                <InputAddon $position="left">$</InputAddon>
                <AdornedInput
                  type="number"
                  min={MIN_TOP_UP_USD}
                  step="1"
                  value={atuAmount}
                  disabled={!autoWanted}
                  onChange={(e) => setAtuAmount(e.target.value)}
                  onBlur={commitAmount}
                />
              </InputWithAddon>
            </AutoField>
          </AutoFieldRow>
          <HelperText aria-live="polite">
            {autoEnabled
              ? autoWanted
                ? `auto top-up is active — we'll top up $${autoAmountUsd} when your balance drops below $${autoThresholdUsd}, using your selected card.`
                : 'auto top-up has been turned off.'
              : autoWanted
                ? `activates when you confirm this top-up — we'll top up $${autoAmountUsd} when your balance drops below $${autoThresholdUsd}, using your selected card. uncheck to opt out.`
                : 'off — you can turn this on anytime.'}
          </HelperText>
          {autoError && <ErrorText>{autoError}</ErrorText>}
        </AutoSection>
        )}

        <GoldNote>
          <GoldNoteText>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <circle cx="12" cy="16" r="1" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            your payment is processed securely. work units will be added to your balance immediately after purchase.
          </GoldNoteText>
        </GoldNote>

        <PromoRow>
          {!promoOpen ? (
            <PromoToggle type="button" onClick={openPromo}>
              have a promo code?
            </PromoToggle>
          ) : (
            <>
              <FormLabel htmlFor="promo-code-input">promo code</FormLabel>
              <PromoForm>
                <PromoInput
                  id="promo-code-input"
                  value={promoCode}
                  onChange={(e) => typePromoCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleRedeemPromo();
                    }
                  }}
                  placeholder="PR0-XXXX-XXXX"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={promoBusy || isBusy}
                />
                <PromoApply
                  type="button"
                  onClick={handleRedeemPromo}
                  disabled={promoBusy || isBusy || !promoCode.trim()}
                >
                  {promoBusy ? 'applying…' : 'apply'}
                </PromoApply>
              </PromoForm>
              {promoError ? (
                <ErrorText role="alert">{promoError}</ErrorText>
              ) : (
                <HelperText>
                  credit lands in your wallet immediately — no card needed.
                </HelperText>
              )}
            </>
          )}
        </PromoRow>

        {errorMsg && <ErrorText role="alert">{errorMsg}</ErrorText>}

        <ModalActions>
          <ModalButton variant="secondary" type="button" onClick={onClose} disabled={status === 'processing'}>
            {status === 'crediting' ? 'close — credit lands shortly' : 'cancel'}
          </ModalButton>
          <ModalButton variant="primary" type="button" onClick={handleSubmit} disabled={isBusy || belowMin}>
            {status === 'processing' ? (
              'processing…'
            ) : status === 'crediting' ? (
              'confirming…'
            ) : (
              <>
                top up <Pr0Unit />
                {effectiveAmountUsd >= MIN_TOP_UP_USD ? ` — ${formatUsd(total)}` : ''}
              </>
            )}
          </ModalButton>
        </ModalActions>
      </>
    );

  const modal = (
    <Backdrop
      data-prest0n-portal="true"
      data-topup-reason={reason || undefined}
      role="dialog"
      aria-modal="true"
      aria-label="Top up pr0"
      // Backdrop click closes (but not while the CHARGE is in flight —
      // 'crediting' may close: payment done, the credit lands regardless).
      onMouseDown={onBackdropMouseDown}
    >
      <Card onMouseDown={(e) => e.stopPropagation()}>{content}</Card>
    </Backdrop>
  );

  return portalTarget ? ReactDOM.createPortal(modal, portalTarget) : null;
};

export default TopUpModal;
